import { Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatOptionModule } from '@angular/material/core';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { PaginatorModule } from 'primeng/paginator';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { map, startWith, takeUntil } from 'rxjs/operators';
import { FormScrapReasonsService } from '../../../../shared/components/form-scrap-reasons/form-scrap-reasons.service';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { BehaviorSubject, combineLatest, Subject } from 'rxjs';
import { ScrapReason } from '../../../../api/elite/types/scrap-reason';
import { User } from '../../../../api/elite/types/users';
import { AlertService } from '../../../../core/services/alert.service';
import { FormScrapReasonsComponent } from '../../../../shared/components/form-scrap-reasons/form-scrap-reasons.component';
import { Shift } from '../../../../api/elite/types/shift';
import { FormScrapReasonData } from '../../../../shared/types/form-scrap-reasons';
import { SchedulerStageItemApiService } from '../../api/table.service';

export type SchedulerStageRegisterProgressTypes = {
  data: {
    maxQuantity: number;
    title: string;
    department: string;
    operatorType?: string;
  };
  return: {
    quantity: number;
    scrap: number;
    shiftId: number;
    scrapReasons: FormScrapReasonData[];
    operatorId?: number;
    machineId?: number;
  };
};

@Component({
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatOptionModule,
    MatProgressSpinnerModule,
    MatSelectModule,
    PaginatorModule,
    ReactiveFormsModule,
    MatSlideToggleModule,
    NgxMatSelectSearchModule,
    FormScrapReasonsComponent,
  ],
  templateUrl: './finish-stage.component.html',
  styleUrls: ['./finish-stage.component.sass'],
  providers: [FormScrapReasonsService],
})
export class SchedulerStageRegisterProgressComponent implements OnInit {
  private readonly data: SchedulerStageRegisterProgressTypes['data'] =
    inject(MAT_DIALOG_DATA);
  private readonly dialogRef = inject(MatDialogRef);
  private readonly apiService = inject(SchedulerStageItemApiService);
  private readonly alertService = inject(AlertService);
  private readonly formScrapReasonsService = inject(FormScrapReasonsService);
  private destroy$ = new Subject<void>();
  private _operators = new BehaviorSubject<User[]>([]);
  maxQuantity = this.data.maxQuantity;

  form = new FormGroup({
    quantity: new FormControl<number>(undefined, [
      Validators.required,
      Validators.min(0),
    ]),
    shiftId: new FormControl<number>(null, Validators.required),
    scrap: new FormControl<number>(0, [Validators.required, Validators.min(0)]),
    searchOperator: new FormControl<string>(undefined),
    operatorId: new FormControl<number>(undefined),
  });

  scrapReasons: ScrapReason[] = [];
  shifts: Shift[] = [];
  readonly operators$ = combineLatest([
    this._operators.asObservable(),
    this.form.get('searchOperator')?.valueChanges.pipe(
      startWith(''),
      map((data) => data.toLowerCase())
    ),
  ]).pipe(
    map(([operators, search]) => {
      if (!search) {
        return operators;
      }
      return operators.filter(
        (op) =>
          op.fullName.toLowerCase().includes(search) ||
          op.number?.toString().toLowerCase().includes(search)
      );
    }),
    takeUntil(this.destroy$)
  );

  showReasons = false;

  get title(): string {
    return this.data.title;
  }

  get disable(): boolean {
    return this.data.maxQuantity === 0;
  }

  get showOperators(): boolean {
    return !!this.data.operatorType;
  }

  constructor() {}

  ngOnInit(): void {
    this.apiService
      .getData(this.data.department, this.data.operatorType)
      .subscribe(
        ({ department, operators }) => {
          this.scrapReasons = department.scrapReasons;
          this.shifts = department.shifts;
          this._operators.next(operators);
          this.formScrapReasonsService.scrapReasons = department.scrapReasons;
        },
        (err) => {
          this.alertService.error('Error al cargar los datos ' + err);
        }
      );

    if (this.showOperators) {
      this.form.get('operatorId').setValidators(Validators.required);
    }

    this.onFormChange();
  }

  onFormChange(): void {
    this.form
      .get('scrap')
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((val: number) => {
        this.formScrapReasonsService.quantity = val;
        if (val <= 0) {
          this.formScrapReasonsService.resetForm();
        }
        this.showReasons = val > 0;
      });
  }

  onClickClose(): void {
    this.dialogRef.close(false);
  }

  onClickSave(): void {
    const { quantity, scrap, operatorId, shiftId } = this.form.value;
    const total = scrap + quantity;
    if (total > this.maxQuantity) {
      this.alertService.error(
        'Estas registrado mas cantidad de la que tienes.'
      );
      return;
    }
    if (total === 0) {
      this.alertService.error('No se puede registrar 0 piezas');
      return;
    }

    if (!this.formScrapReasonsService.isQuantityValid()) {
      this.alertService.error(
        `Las cantidades distribuidas en las razones de scrap no coinciden`
      );
      return;
    }
    if (!this.formScrapReasonsService.isFormValid() && scrap > 0) {
      this.alertService.error(`Formulario de scrap invalido`);
      return;
    }

    const response = {
      quantity,
      scrap,
      shiftId,
      operatorId,
      scrapReasons: this.showReasons
        ? this.formScrapReasonsService.getData()
        : undefined,
    };
    this.dialogRef.close(response);
  }
}
